
import React from "react";
import { LoadingButton } from "@/components/ui/loading-button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import TwilioErrorAlert from "./number/TwilioErrorAlert";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";

type Props = {
  error: string | null;
  subResult: string | null;
  creatingSub: boolean;
  handleSubaccount: () => void;
  onNext: () => void;
};

const BusinessOwnerTwilioSetup: React.FC<Props> = ({
  error,
  subResult,
  creatingSub,
  handleSubaccount,
  onNext,
}) => {
  const isEncryptionError = error?.toLowerCase().includes("encryption") || 
                          subResult?.toLowerCase().includes("limited") || 
                          subResult?.toLowerCase().includes("encryption");
                          
  return (
    <div>
      <h2 className="text-xl font-semibold mb-2">One-click Twilio Setup</h2>
      <p className="mb-3">
        Calie.ai can auto-create you a secure Twilio sub-account instantly. Just click below—no manual setup needed.
      </p>
      
      {error && !isEncryptionError && <TwilioErrorAlert error={error} />}
      
      {isEncryptionError && (
        <Alert 
          variant="destructive" 
          className="mb-4 border-yellow-500 bg-yellow-50 text-yellow-900"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Encryption Configuration Issue</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {error || "Calie.ai needs to configure encryption keys for secure credential storage."}
            </p>
            <div className="text-sm bg-yellow-100 p-2 rounded mt-2">
              <p className="font-medium">What this means:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>Your Twilio account was created successfully</li>
                <li>You can continue with setup and create tracking numbers</li>
                <li>The system administrator will fix encryption soon</li>
              </ul>
            </div>
            <div className="flex justify-end mt-2">
              <LoadingButton 
                variant="outline" 
                onClick={onNext} 
                type="button"
                className="bg-yellow-100 hover:bg-yellow-200 border-yellow-300"
              >
                Continue with Limited Setup
              </LoadingButton>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {subResult && !isEncryptionError && (
        <Alert 
          variant="default" 
          className={cn(
            "mb-4", 
            subResult.includes("limited") ? "border-yellow-500 bg-yellow-50 text-yellow-900" : ""
          )}
        >
          <AlertTitle>{subResult.includes("limited") ? "Partial Success" : "Success"}</AlertTitle>
          <AlertDescription>
            {subResult}
            {subResult.includes("limited") && (
              <div className="mt-2 text-sm text-yellow-700">
                The system administrator needs to configure encryption settings. You can continue with setup, but some features may be limited.
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {!subResult && (
        <>
          <LoadingButton loading={creatingSub} onClick={handleSubaccount} type="button" disabled={creatingSub}>
            {creatingSub ? "Creating Twilio Sub-Account..." : "Create My Number"}
          </LoadingButton>
          <div className="text-xs mt-2 text-muted-foreground">
            Powered by Twilio • Your numbers will be ready in seconds.
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessOwnerTwilioSetup;
